import { ErrorNote, Routes, Spinner, useApi, useAsync, type RouteTable } from '@khaddi/ui';
import { useCallback, useMemo, useState } from 'react';
import { View } from 'react-native';
import { ProfileProvider } from './profile';
import { AccountScreen } from './screens/AccountScreen';
import { ApplicationStatusScreen } from './screens/ApplicationStatusScreen';
import { DeliveryScreen } from './screens/DeliveryScreen';
import { EarningsScreen } from './screens/EarningsScreen';
import { HistoryScreen } from './screens/HistoryScreen';
import { HomeScreen } from './screens/HomeScreen';
import { SignupScreen } from './screens/SignupScreen';

const routes: RouteTable = [
  { pattern: '/delivery/:orderId', render: ({ orderId }) => <DeliveryScreen orderId={orderId ?? ''} key={orderId} /> },
  { pattern: '/history', render: () => <HistoryScreen /> },
  { pattern: '/earnings', render: () => <EarningsScreen /> },
  { pattern: '/account', render: () => <AccountScreen /> },
];

/** Decides what a signed-in phone sees: onboarding, application status, or the working app. */
export const ShipperGate = () => {
  const api = useApi();
  const me = useAsync(() => api.shipper.me(), []);
  const [reapplying, setReapplying] = useState(false);
  const { setData } = me;
  const reload = useCallback(async () => {
    setData(await api.shipper.me());
  }, [api, setData]);

  const profile = me.data?.profile ?? null;
  const contextValue = useMemo(() => (profile ? { profile, reload } : null), [profile, reload]);

  if (me.loading && !me.data) return <Spinner />;
  if (me.error && !me.data) {
    return (
      <View style={{ flex: 1, justifyContent: 'center' }}>
        <ErrorNote message={me.error} onRetry={() => void me.reload()} />
      </View>
    );
  }

  const onSubmitted = (next: NonNullable<typeof profile>) => {
    setReapplying(false);
    setData({ profile: next });
  };

  if (!profile || (profile.status === 'rejected' && reapplying)) {
    return <SignupScreen previous={profile} onSubmitted={onSubmitted} onCancel={profile ? () => setReapplying(false) : undefined} />;
  }
  if (profile.status !== 'active' || !contextValue) {
    return <ApplicationStatusScreen profile={profile} onRefresh={reload} onReapply={() => setReapplying(true)} />;
  }
  return (
    <ProfileProvider value={contextValue}>
      <Routes table={routes} fallback={<HomeScreen />} />
    </ProfileProvider>
  );
};
